import React, { useState, useEffect } from 'react'
import { requestPasswordReset, resetPassword, validatePasswordResetToken } from '../../api/authApi'

interface PasswordResetFormProps {
  onBack: () => void
  onSuccess: (message: string) => void
  onError: (message: string) => void
}

interface PasswordResetConfirmFormProps {
  token: string
  onBack: () => void
  onSuccess: (message: string) => void
  onError: (message: string) => void
}

export const PasswordResetRequestForm: React.FC<PasswordResetFormProps> = ({
  onBack,
  onSuccess,
  onError
}) => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const errors: Record<string, string> = {}
    if (!validateEmail(email)) {
      errors.email = 'Введите корректный email'
    }

    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    setIsLoading(true)

    try {
      const response = await requestPasswordReset(email)

      if (response.success) {
        onSuccess(response.message)
      } else {
        onError(response.message || 'Ошибка при запросе восстановления')
      }
    } catch (error: any) {
      onError(error.response?.data?.message || 'Произошла ошибка при отправке запроса')
    } finally {
      setIsLoading(false)
    }
  }

  const styles = {
    container: {
      width: '100%',
      maxWidth: '360px',
    },
    formGroup: {
      marginBottom: '1rem'
    },
    label: {
      display: 'block',
      color: '#374151',
      fontWeight: 500,
      marginBottom: '0.4rem',
      fontSize: '0.875rem'
    },
    input: {
      width: '100%',
      padding: '0.6rem 0.8rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.95rem',
      transition: 'all 0.2s ease',
      background: '#ffffff',
      color: '#000000',
      boxSizing: 'border-box' as const
    },
    inputError: {
      borderColor: '#ef4444',
      background: '#fef2f2'
    },
    errorMessage: {
      color: '#ef4444',
      fontSize: '0.8rem',
      marginTop: '0.3rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem'
    },
    button: {
      width: '100%',
      padding: '0.7rem 1rem',
      border: 'none',
      borderRadius: '6px',
      fontWeight: 600,
      fontSize: '0.9rem',
      cursor: 'pointer',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.4rem'
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
    backButton: {
      background: 'transparent',
      color: '#6b7280',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.8rem',
      marginBottom: '0.8rem',
      padding: '0.3rem 0'
    },
    infoBox: {
      background: '#f0f9ff',
      border: '1px solid #0ea5e9',
      borderRadius: '6px',
      padding: '0.8rem',
      marginBottom: '1rem',
      fontSize: '0.8rem',
      color: '#075985'
    }
  }

  return (
    <div style={styles.container}>
      <button
        type="button"
        onClick={onBack}
        style={styles.backButton}
      >
        ← Назад к входу
      </button>

     

      <div style={styles.infoBox}>
        💡 Введите email, который вы использовали при регистрации. На него будет отправлена ссылка для сброса пароля.
      </div>

      <form onSubmit={handleSubmit}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Email *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              setFieldErrors({})
            }}
            style={{
              ...styles.input,
              ...(fieldErrors.email ? styles.inputError : {})
            }}
            placeholder="ivan@example.com"
            required
            autoFocus
          />
          {fieldErrors.email && (
            <div style={styles.errorMessage}>
              <span>⚠️</span> {fieldErrors.email}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={{
            ...styles.button,
            ...(isLoading ? styles.buttonDisabled : {})
          }}
        >
          {isLoading ? (
            <>
              <span>⏳</span> Отправляем...
            </>
          ) : (
            <>
              <span>📧</span> Отправить ссылку для восстановления
            </>
          )}
        </button>
      </form>
    </div>
  )
}

export const PasswordResetConfirmForm: React.FC<PasswordResetConfirmFormProps> = ({
  token,
  onBack,
  onSuccess,
  onError
}) => {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isValidatingToken, setIsValidatingToken] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [userInfo, setUserInfo] = useState<{ name: string; email: string } | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [initialValidationDone, setInitialValidationDone] = useState(false)

  // Валидация токена при загрузке компонента (только один раз)
  useEffect(() => {
    let isMounted = true

    const validateToken = async () => {
      if (!isMounted) return
      
      setIsValidatingToken(true)
      try {
        const response = await validatePasswordResetToken(token)
        
        if (!isMounted) return
        
        if (response.success && response.user) {
          setTokenValid(true)
          setUserInfo({
            name: response.user.name,
            email: response.user.email
          })
        } else {
          setTokenValid(false)
          setError(response.message || 'Недействительная ссылка для восстановления пароля')
        }
      } catch (error) {
        if (!isMounted) return
        setTokenValid(false)
        setError('Произошла ошибка при проверке ссылки')
      } finally {
        if (isMounted) {
          setIsValidatingToken(false)
          setInitialValidationDone(true) // Отмечаем, что валидация выполнена
        }
      }
    }

    // Валидируем токен только если пароль еще не был сброшен и валидация еще не выполнялась
    if (!passwordResetSuccess && !initialValidationDone) {
      validateToken()
    }

    return () => {
      isMounted = false
    }
  }, [token]) // Убираем passwordResetSuccess из зависимостей

  // Если пароль успешно сброшен, показываем только сообщение об успехе
  if (passwordResetSuccess) {
    return (
      <div style={{
        width: '100%',
        maxWidth: '300px',
        textAlign: 'center' as const,
        padding: '0.8rem'
      }}>
        <div style={{
          background: '#f0fdf4',
          border: '1px solid #22c55e',
          borderRadius: '6px',
          padding: '0.6rem',
          marginBottom: '0.6rem'
        }}>
          <p style={{ color: '#15803d', margin: 0, fontSize: '0.85rem', fontWeight: 500 }}>
            ✅ Пароль успешно изменен!
          </p>
        </div>
        <a
          href="https://cabinet.advatool.ru/login"
          style={{
            display: 'inline-block',
            padding: '0.5rem 1rem',
            border: 'none',
            borderRadius: '6px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            textDecoration: 'none',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: 600,
            transition: 'all 0.2s ease'
          }}
        >
          🚀 Перейти к входу
        </a>
      </div>
    )
  }

  // Показываем загрузку валидации токена
  if (isValidatingToken) {
    return (
      <div style={{
        width: '100%',
        maxWidth: '360px',
        textAlign: 'center' as const,
        padding: '1.5rem'
      }}>
        <div style={{
          display: 'inline-block',
          width: '32px',
          height: '32px',
          border: '3px solid #f3f4f6',
          borderTop: '3px solid #667eea',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '0.8rem'
        }}></div>
        <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
          Проверяем ссылку восстановления...
        </p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    )
  }

  // Если токен недействителен, не показываем форму
  if (!tokenValid) {
    return (
      <div style={{
        width: '100%',
        maxWidth: '320px',
        textAlign: 'center' as const,
        padding: '1rem'
      }}>
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          padding: '0.6rem',
          marginBottom: '0.6rem'
        }}>
          <p style={{ color: '#dc2626', margin: 0, fontSize: '0.85rem' }}>
            ❌ Ссылка недействительна или истекла
          </p>
        </div>
        <button
          onClick={onBack}
          style={{
            padding: '0.5rem 1rem',
            border: 'none',
            borderRadius: '6px',
            background: '#6b7280',
            color: 'white',
            cursor: 'pointer',
            fontSize: '0.85rem'
          }}
        >
          Вернуться назад
        </button>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const errors: Record<string, string> = {}
    if (!password || password.length < 6) {
      errors.password = 'Пароль должен содержать минимум 6 символов'
    }
    if (password !== confirmPassword) {
      errors.confirmPassword = 'Пароли не совпадают'
    }

    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    setIsLoading(true)

    try {
      const response = await resetPassword(token, password)

      if (response.success) {
        setPasswordResetSuccess(true) // Отмечаем успешный сброс
        setError(null) // Очищаем ошибки
        onSuccess(response.message)
      } else {
        setError(response.message || 'Ошибка при сбросе пароля')
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Произошла ошибка при сбросе пароля')
    } finally {
      setIsLoading(false)
    }
  }

  const styles = {
    container: {
      width: '100%',
      maxWidth: '360px',
    },
    formGroup: {
      marginBottom: '1rem'
    },
    label: {
      display: 'block',
      color: '#374151',
      fontWeight: 500,
      marginBottom: '0.4rem',
      fontSize: '0.875rem'
    },
    input: {
      width: '100%',
      padding: '0.6rem 0.8rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.95rem',
      transition: 'all 0.2s ease',
      background: '#ffffff',
      color: '#000000',
      boxSizing: 'border-box' as const
    },
    inputError: {
      borderColor: '#ef4444',
      background: '#fef2f2'
    },
    errorMessage: {
      color: '#ef4444',
      fontSize: '0.8rem',
      marginTop: '0.3rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem'
    },
    button: {
      width: '100%',
      padding: '0.7rem 1rem',
      border: 'none',
      borderRadius: '6px',
      fontWeight: 600,
      fontSize: '0.9rem',
      cursor: 'pointer',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.4rem'
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
    backButton: {
      background: 'transparent',
      color: '#6b7280',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.8rem',
      marginBottom: '0.8rem',
      padding: '0.3rem 0'
    },
    infoBox: {
      background: '#f0fdf4',
      border: '1px solid #22c55e',
      borderRadius: '6px',
      padding: '0.8rem',
      marginBottom: '1rem',
      fontSize: '0.8rem',
      color: '#15803d'
    }
  }

  return (
    <div style={styles.container}>
      <button
        type="button"
        onClick={onBack}
        style={styles.backButton}
      >
      
      </button>

      <h2 style={{ textAlign: 'center', marginBottom: '0.8rem', color: '#374151', fontSize: '1.1rem' }}>
        🔐 Новый пароль
      </h2>

      {userInfo && (
        <div style={styles.infoBox}>
          <p style={{ margin: 0, fontWeight: 500 }}>
            ✅ Восстановление пароля для: {userInfo.name}
          </p>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', opacity: 0.8 }}>
            Email: {userInfo.email}
          </p>
        </div>
      )}

      {/* Отображение ошибки */}
      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          padding: '0.6rem',
          marginBottom: '1rem',
          fontSize: '0.85rem',
          color: '#dc2626'
        }}>
          ❌ {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Новый пароль *</label>
          <input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              setFieldErrors({})
              setError(null) // Очищаем ошибку при изменении
            }}
            style={{
              ...styles.input,
              ...(fieldErrors.password ? styles.inputError : {})
            }}
            placeholder="Минимум 6 символов"
            required
            autoFocus
          />
          {fieldErrors.password && (
            <div style={styles.errorMessage}>
              <span>⚠️</span> {fieldErrors.password}
            </div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Подтвердите пароль *</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value)
              setFieldErrors({})
              setError(null) // Очищаем ошибку при изменении
            }}
            style={{
              ...styles.input,
              ...(fieldErrors.confirmPassword ? styles.inputError : {})
            }}
            placeholder="Повторите новый пароль"
            required
          />
          {fieldErrors.confirmPassword && (
            <div style={styles.errorMessage}>
              <span>⚠️</span> {fieldErrors.confirmPassword}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={{
            ...styles.button,
            ...(isLoading ? styles.buttonDisabled : {})
          }}
        >
          {isLoading ? (
            <>
              <span>⏳</span> Сохраняем...
            </>
          ) : (
            <>
              <span>💾</span> Сохранить новый пароль
            </>
          )}
        </button>
      </form>
    </div>
  )
}
